import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/lib/api'
import { getToken } from '../lib/authToken'
import { CloudNavBar } from '../components/CloudNavBar'
import { Loader2 } from '@ensafely/checker-ui'

const DAY_MS = 24 * 60 * 60 * 1000
const MAX_PERIOD_DAYS = 366 // 1 year, inclusive of leap years

function toInputDate(d: Date): string {
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function presetRange(days: number): { start: string; end: string } {
  const end = new Date()
  const start = new Date(end.getTime() - days * DAY_MS)
  return { start: toInputDate(start), end: toInputDate(end) }
}

interface ValidationResult {
  valid: boolean
  error: string | null
}

function validatePeriod(start: string, end: string): ValidationResult {
  if (!start || !end) return { valid: false, error: null }
  const s = new Date(`${start}T00:00:00Z`)
  const e = new Date(`${end}T00:00:00Z`)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) {
    return { valid: false, error: 'Invalid date.' }
  }
  if (e.getTime() < s.getTime()) {
    return { valid: false, error: 'End date must be on or after start date.' }
  }
  const days = (e.getTime() - s.getTime()) / DAY_MS
  if (days > MAX_PERIOD_DAYS) {
    return { valid: false, error: 'Period must not exceed 1 year.' }
  }
  return { valid: true, error: null }
}

export default function ReportsPage() {
  const initial = presetRange(30)
  const [start, setStart] = useState(initial.start)
  const [end, setEnd] = useState(initial.end)
  const [project, setProject] = useState('')
  const [projects, setProjects] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const downloadBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    api
      .getProjects()
      .then((p) => setProjects(p || []))
      .catch(() => {
        // Non-fatal: scope selector falls back to "All checks" only.
      })
  }, [])

  const validation = useMemo(() => validatePeriod(start, end), [start, end])

  const applyPreset = (days: number) => {
    const r = presetRange(days)
    setStart(r.start)
    setEnd(r.end)
    setError(null)
    // Defer focus until the inputs settle.
    setTimeout(() => downloadBtnRef.current?.focus(), 0)
  }

  const handleDownload = async () => {
    if (!validation.valid) return
    setError(null)
    setLoading(true)
    try {
      const params = new URLSearchParams({ start, end })
      if (project) params.set('project', project)
      const url = `/api/tenants/current/reports/uptime.pdf?${params.toString()}`
      const token = getToken()
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) {
        let msg = `Request failed (${res.status})`
        const ct = res.headers.get('content-type') || ''
        if (ct.includes('application/json')) {
          try {
            const body = await res.json()
            if (body?.error) msg = body.error
          } catch {
            // ignore JSON parse failure
          }
        } else {
          const text = await res.text()
          if (text && text.length < 500) msg = text
        }
        throw new Error(msg)
      }
      const blob = await res.blob()
      const cd = res.headers.get('Content-Disposition') || ''
      const match = /filename="?([^";]+)"?/.exec(cd)
      const filename = match?.[1] || `uptime-${start}-to-${end}.pdf`
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(blobUrl)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to download report.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const downloadDisabled = !validation.valid || loading

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-3xl mx-auto py-8 px-6">
        <h1 className="text-2xl font-bold mb-6">Uptime Report</h1>
        <div className="bg-card rounded-lg shadow p-6 space-y-6">
          <p className="text-sm text-muted-foreground">
            Generate an on-demand uptime PDF for the selected period.
          </p>

          {error && (
            <p role="alert" className="text-sm text-red-600 bg-red-50 rounded p-2">
              {error}
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-sm font-medium mb-1">Start date</span>
              <input
                type="date"
                aria-label="Start date"
                className="w-full border rounded px-3 py-2 text-sm bg-background"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1">End date</span>
              <input
                type="date"
                aria-label="End date"
                className="w-full border rounded px-3 py-2 text-sm bg-background"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>

          {validation.error && (
            <p className="text-sm text-red-600">{validation.error}</p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => applyPreset(7)}
              className="px-3 py-1.5 text-xs border rounded hover:bg-muted"
            >
              Last 7 days
            </button>
            <button
              type="button"
              onClick={() => applyPreset(30)}
              className="px-3 py-1.5 text-xs border rounded hover:bg-muted"
            >
              Last 30 days
            </button>
            <button
              type="button"
              onClick={() => applyPreset(90)}
              className="px-3 py-1.5 text-xs border rounded hover:bg-muted"
            >
              Last 90 days
            </button>
          </div>

          <label className="block">
            <span className="block text-sm font-medium mb-1">Scope</span>
            <select
              aria-label="Scope"
              className="w-full border rounded px-3 py-2 text-sm bg-background"
              value={project}
              onChange={(e) => setProject(e.target.value)}
            >
              <option value="">All checks</option>
              {projects.map((p) => (
                <option key={p} value={p}>
                  Project: {p}
                </option>
              ))}
            </select>
          </label>

          <div className="pt-2">
            <button
              ref={downloadBtnRef}
              type="button"
              onClick={handleDownload}
              disabled={downloadDisabled}
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {loading ? 'Generating...' : 'Download PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
